import React, { useState } from 'react';
import OrderBanner from 'components/orders/orderBanner/OrderBanner';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import { BsLightningFill } from 'react-icons/bs';
import { OrderTypeProvider, ORDER_TYPES } from 'views/admin/pm-main/comex/detail/context/OrderTypeContext';
import AdvisoryView from 'views/admin/pm-main/comex/detail/strategies/AdvisoryView';
import AccountAndOrderView from 'views/admin/pm-main/comex/detail/strategies/AccountAndOrderView';
import EncomendaView from 'views/admin/pm-main/comex/detail/strategies/EncomendaView';
import DistributionView from 'views/admin/pm-main/comex/detail/strategies/DistributionView';
import ProductTax from 'components/comex/ProductTax/ProductTax';
import TaxPrerequisitesDialog from 'components/dialogs/TaxPrerequisitesDialog';
import useTaxCalculation from 'hooks/useTaxCalculation';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import PrintHeader from 'views/admin/pm-main/comex/detail/components/PrintHeader';

// TabPanel component to display the corresponding tab content
function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Card extra={'w-full h-full bg-white'}>
                    {children}
                </Card>
            )}
        </div>
    );
}

// Component to render the appropriate view based on order type
const OrderTypeStrategy = ({ order }) => {
    const orderType = order?.order_type;

    // For the Overview tab, use the appropriate strategy
    if (orderType === ORDER_TYPES.ADVISORY) {
        return <AdvisoryView order={order} />;
    }
    if (orderType === ORDER_TYPES.ACCOUNT_AND_ORDER) {
        return <AccountAndOrderView order={order} />;
    }
    if (orderType === ORDER_TYPES.ORDER) {
        return <EncomendaView order={order} />;
    }
    if (orderType === ORDER_TYPES.DISTRIBUTION) {
        return <DistributionView order={order} />;
    }
    // Add other strategies as they are implemented
    return <Box p={2}>Strategy not implemented for this order type yet</Box>;
};

const OrderView = (props) => {
    const { order } = props;
    const [activeTab, setActiveTab] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();
    
    // Use the tax calculation hook
    const {
        checkingOrderId,
        prerequisitesData,
        prerequisitesDialogOpen,
        handleCalculateTaxes,
        hasOrderTaxCalculation,
        closePrerequisitesDialog
    } = useTaxCalculation();
    
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };
    
    const hasTaxCalculation = hasOrderTaxCalculation(order.id);

    return (
        <OrderTypeProvider order={order}>
            <Box mt={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12} className="no-print">
                        <OrderBanner order={order} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box className="no-print" mb={2}>
                            <Stack 
                                direction="row" 
                                justifyContent="space-between" 
                                alignItems="center"
                                spacing={2}
                            >
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', flex: 1, minWidth: 0 }}>
                                    <Tabs 
                                        value={activeTab} 
                                        onChange={handleTabChange}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        allowScrollButtonsMobile
                                    >
                                        <Tab label="Overview" />
                                        <Tab label="Product Tax" />
                                    </Tabs>
                                </Box>
                                <Tooltip 
                                    title={isMobile ? (hasTaxCalculation ? 'Recalculate Taxes' : 'Run Tax Calculation') : ''}
                                    placement="bottom"
                                >
                                    <Button
                                        variant="outlined"
                                        startIcon={!isMobile && checkingOrderId !== order.id ? <BsLightningFill size={16} /> : null}
                                        onClick={() => handleCalculateTaxes(order.id, {
                                            onSuccess: () => {
                                                // Invalidate the latest tax calculation query to trigger a refetch
                                                queryClient.invalidateQueries(['tax/order-tax-calculation/latest', String(order.id)]);
                                            }
                                        })}
                                        disabled={checkingOrderId === order.id}
                                        className="no-print"
                                        sx={{
                                            minWidth: { xs: 40, sm: 140, md: 180 },
                                            width: { xs: 40, sm: 'auto' },
                                            height: { xs: 40, sm: 36 },
                                            px: { xs: 0, sm: 3 },
                                            py: { xs: 0, sm: 1 },
                                            fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                                            borderRadius: { xs: '50%', sm: 2 },
                                            textTransform: 'none',
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                                borderColor: theme.palette.primary.dark,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            },
                                            '&.Mui-disabled': {
                                                borderColor: theme.palette.action.disabled,
                                            }
                                        }}
                                    >
                                        {checkingOrderId === order.id ? (
                                            <CircularProgress size={isMobile ? 18 : 16} />
                                        ) : (
                                            <>
                                                {isMobile ? (
                                                    <BsLightningFill size={18} />
                                                ) : (
                                                    hasTaxCalculation ? 'Recalculate Taxes' : 'Run Tax Calculation'
                                                )}
                                            </>
                                        )}
                                    </Button>
                                </Tooltip>
                            </Stack>
                        </Box>
                        <TabPanel value={activeTab} index={0}>
                            <PrintHeader order={order} />
                            <OrderTypeStrategy order={order} />
                        </TabPanel>
                        <TabPanel value={activeTab} index={1}>
                            <ProductTax formData={order} />
                        </TabPanel>
                    </Grid>
                </Grid>
            </Box>
            
            {/* Prerequisites Dialog */}
            <TaxPrerequisitesDialog
                open={prerequisitesDialogOpen}
                onClose={closePrerequisitesDialog}
                prerequisites={prerequisitesData}
            />
        </OrderTypeProvider>
    );
};

export default OrderView;